/**
 * Light Presence Service
 * Manages real-time state synchronization between clients using Supabase Realtime Presence
 * 
 * Architecture:
 * - All clients broadcast light state changes via presence
 * - Leader client applies changes to bridge and persists to DB
 * - Follower clients only update local state
 * 
 * Note: This service is only available in online mode. In standalone mode,
 * there's only one client so presence is not needed.
 */

import { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { realtimeLogger } from '@/lib/logger'
import { isStandaloneMode } from '@/lib/dataProvider'

export interface LightState {
  on?: boolean
  brightness?: number
  hue?: number
  saturation?: number
  ct?: number
  xy?: [number, number]
}

export interface PresencePayload {
  lightId: string
  state: LightState
  timestamp: number
  clientId: string
  sceneId: string
}

export interface PeerState {
  clientId: string
  userId: string
  online_at: string
}

type StateChangeCallback = (payload: PresencePayload) => void
type PeerJoinCallback = (clientId: string, state: PeerState) => void
type PeerLeaveCallback = (clientId: string) => void

export class LightPresenceService {
  private channel: RealtimeChannel | null = null
  private clientId: string
  private userId: string | null = null
  private currentSceneId: string | null = null
  private stateChangeCallbacks: Set<StateChangeCallback> = new Set()
  private peerJoinCallbacks: Set<PeerJoinCallback> = new Set()
  private peerLeaveCallbacks: Set<PeerLeaveCallback> = new Set()
  private connectedPeers: Map<string, PeerState> = new Map()
  private isInitialized = false

  constructor() {
    this.clientId = this.generateClientId()
  }

  /**
   * Generate a unique client ID for this browser session
   */
  private generateClientId(): string {
    return `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Initialize presence channel for a specific scene
   * Note: In standalone mode, presence is disabled (single client, no coordination needed)
   */
  async initialize(userId: string, sceneId: string): Promise<void> {
    // Skip initialization in standalone mode - presence requires cloud backend
    if (isStandaloneMode()) {
      realtimeLogger.info(`📡 [LightPresence] Skipping initialization in standalone mode`)
      this.isInitialized = false
      return
    }
    
    if (this.isInitialized && this.currentSceneId === sceneId) {
      realtimeLogger.info(`📡 [LightPresence] Already initialized for scene ${sceneId}`)
      return
    }

    // Clean up existing channel if switching scenes
    if (this.channel) {
      await this.cleanup()
    }

    this.userId = userId
    this.currentSceneId = sceneId
    this.connectedPeers.clear()

    const channelName = `light-presence:${sceneId}`
    realtimeLogger.info(`📡 [LightPresence] Initializing channel: ${channelName}`)

    this.channel = supabase.channel(channelName, {
      config: {
        presence: {
          key: this.clientId
        }
      }
    })

    // Subscribe to presence state changes
    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel!.presenceState()
        realtimeLogger.info(`👥 [LightPresence] Presence sync:`, Object.keys(state).length, 'peers')
        
        // Update connected peers list
        this.connectedPeers.clear()
        for (const [clientId, presences] of Object.entries(state)) {
          if (presences && presences.length > 0) {
            this.connectedPeers.set(clientId, presences[0] as unknown as PeerState)
          }
        }
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        realtimeLogger.info(`✅ [LightPresence] Peer joined: ${key}`)
        if (newPresences && newPresences.length > 0) {
          const peerState = newPresences[0] as unknown as PeerState
          this.connectedPeers.set(key, peerState)
          
          // Notify callbacks
          for (const callback of this.peerJoinCallbacks) {
            callback(key, peerState)
          }
        }
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        realtimeLogger.info(`❌ [LightPresence] Peer left: ${key}`)
        this.connectedPeers.delete(key)
        
        // Notify callbacks
        for (const callback of this.peerLeaveCallbacks) {
          callback(key)
        }
      })
      .on('broadcast', { event: 'light-state' }, ({ payload }) => {
        // Ignore our own broadcasts
        if (payload.clientId === this.clientId) {
          return
        }

        realtimeLogger.info(`📨 [LightPresence] Received state from ${payload.clientId}:`, payload.lightId)
        
        // Notify all registered callbacks
        for (const callback of this.stateChangeCallbacks) {
          callback(payload as PresencePayload)
        }
      })
      .on('broadcast', { event: 'groups-changed' }, ({ payload }) => {
        // Ignore our own broadcasts
        if (payload.clientId === this.clientId) {
          return
        }

        realtimeLogger.info(`📨 [LightPresence] Groups changed, triggering refetch`)
        
        // Notify group change (we'll handle this in the component)
        window.dispatchEvent(new CustomEvent('light-groups-changed', { detail: payload }))
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          realtimeLogger.info(`✅ [LightPresence] Subscribed to ${channelName}`)
          
          // Track our presence
          await this.channel!.track({
            clientId: this.clientId,
            userId: this.userId,
            online_at: new Date().toISOString()
          })
          
          this.isInitialized = true
        } else if (status === 'CHANNEL_ERROR') {
          realtimeLogger.error(`❌ [LightPresence] Channel error`)
          this.isInitialized = false
        } else if (status === 'TIMED_OUT') {
          realtimeLogger.error(`❌ [LightPresence] Channel timed out`)
          this.isInitialized = false
        }
      })
  }

  /**
   * Broadcast light state change to all connected clients
   */
  async broadcastLightState(lightId: string, state: LightState): Promise<void> {
    if (!this.channel || !this.isInitialized) {
      realtimeLogger.warn(`⚠️ [LightPresence] Cannot broadcast - not initialized`)
      return
    }

    if (!this.currentSceneId) {
      realtimeLogger.warn(`⚠️ [LightPresence] Cannot broadcast - no active scene`)
      return
    }

    const payload: PresencePayload = {
      lightId,
      state,
      timestamp: Date.now(),
      clientId: this.clientId,
      sceneId: this.currentSceneId
    }

    try {
      await this.channel.send({
        type: 'broadcast',
        event: 'light-state',
        payload
      })
      
      realtimeLogger.info(`📤 [LightPresence] Broadcasted state for ${lightId}`)
    } catch (error) {
      realtimeLogger.error(`❌ [LightPresence] Failed to broadcast:`, error)
    }
  }

  /**
   * Broadcast multiple light states at once (batch)
   */
  async broadcastBatch(updates: Array<{ lightId: string; state: LightState }>): Promise<void> {
    if (!this.channel || !this.isInitialized || !this.currentSceneId) {
      realtimeLogger.warn(`⚠️ [LightPresence] Cannot broadcast batch - not initialized`)
      return
    }

    try {
      const payload = {
        type: 'batch',
        sceneId: this.currentSceneId,
        updates,
        timestamp: Date.now(),
        clientId: this.clientId
      }

      await this.channel.send({
        type: 'broadcast',
        event: 'light-state-batch',
        payload
      })
      
      realtimeLogger.info(`📤 [LightPresence] Broadcasted batch of ${updates.length} updates`)
    } catch (error) {
      realtimeLogger.error(`❌ [LightPresence] Failed to broadcast batch:`, error)
    }
  }

  /**
   * Broadcast that groups have changed (for splits, merges, deletions)
   */
  async broadcastGroupsChanged(): Promise<void> {
    if (!this.channel || !this.isInitialized) {
      realtimeLogger.warn(`⚠️ [LightPresence] Cannot broadcast groups - not initialized`)
      return
    }

    try {
      await this.channel.send({
        type: 'broadcast',
        event: 'groups-changed',
        payload: {
          clientId: this.clientId,
          timestamp: Date.now()
        }
      })
      
      realtimeLogger.info(`📤 [LightPresence] Broadcasted groups-changed event`)
    } catch (error) {
      realtimeLogger.error(`❌ [LightPresence] Failed to broadcast groups changed:`, error)
    }
  }

  /**
   * Subscribe to state changes from other clients
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeCallbacks.add(callback)
    
    // Return unsubscribe function
    return () => {
      this.stateChangeCallbacks.delete(callback)
    }
  }

  /**
   * Subscribe to peer join events
   */
  onPeerJoin(callback: PeerJoinCallback): () => void {
    this.peerJoinCallbacks.add(callback)
    return () => {
      this.peerJoinCallbacks.delete(callback)
    }
  }

  /**
   * Subscribe to peer leave events
   */
  onPeerLeave(callback: PeerLeaveCallback): () => void {
    this.peerLeaveCallbacks.add(callback)
    return () => {
      this.peerLeaveCallbacks.delete(callback)
    }
  }

  /**
   * Get list of currently connected peers (excluding self)
   */
  getConnectedPeers(): PeerState[] {
    return Array.from(this.connectedPeers.values())
      .filter(peer => peer.clientId !== this.clientId)
  }

  /**
   * Get this client's ID
   */
  getClientId(): string {
    return this.clientId
  }

  /**
   * Get current scene ID
   */
  getCurrentSceneId(): string | null {
    return this.currentSceneId
  }

  /**
   * Check if service is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.channel !== null
  }

  /**
   * Clean up presence channel
   */
  async cleanup(): Promise<void> {
    realtimeLogger.info(`🧹 [LightPresence] Cleaning up channel`)
    
    if (this.channel) {
      await this.channel.untrack()
      await this.channel.unsubscribe()
      this.channel = null
    }

    this.isInitialized = false
    this.currentSceneId = null
    this.connectedPeers.clear()
    this.stateChangeCallbacks.clear()
    this.peerJoinCallbacks.clear()
    this.peerLeaveCallbacks.clear()
  }
}

// Singleton instance
export const lightPresenceService = new LightPresenceService()
